/**
 * DataDictionaryView -- the non-SoR ingest field reference (P117.1).
 *
 * Route: /admin/data-dictionary
 *
 * Answers the customer ask "show me what you expect, format and
 * fields". Renders the field-spec registry behind
 * GET /v1/admin/foundation/import/dictionary: one card per importable
 * entity, a table of its columns (required vs optional, type, enum
 * values, SAMA-mandated chip, description), and a Download-template
 * button that streams the per-entity CSV (header + one example row).
 *
 * Admin-only (the endpoint 403s otherwise). Hardcoded English to match
 * the sibling shared/admin views (AuditEventsView / ConnectorsView /
 * SettingsAdminView). The import / dry-run / commit surface lands in
 * P117.2/.3; this is the read-only reference that precedes it.
 */
import { useTranslation } from "react-i18next";

import { Arrow } from "shared/components/Arrow";
import { PathTrail } from "shared/components/PathTrail";
import { useState } from "react";
import { AdminOnlyPanel } from "shared/admin/AdminOnlyPanel";
import { said } from "shared/i18n/said";
import { Link } from "react-router-dom";

import { MarsadIcon } from "shared/components/MarsadIcon";
import { useMe } from "shared/api/useMe";
import {
  downloadImportTemplate,
  useImportDictionary,
} from "shared/api/hooks/useImportDictionary";
import type { ImportEntityDictionary, ImportFieldSpec } from "shared/api/types";
import { useToast } from "shared/toast/ToastProvider";

import { Identifier } from "shared/components/Identifier";
/** The schema's word for a reference field -- the same vocabulary the
 *  other chips print straight out of `field.type`. */
const REF_TYPE = "ref";

function TypeChip({ field }: { field: ImportFieldSpec }) {
  /* Round 11 F5 -- this arrow is chosen by the reader's direction now.
     The page carried 26 of them and only the five in the edge headings
     mirrored, because those go through <Arrow> and these were a
     hard-coded U+2192 in a template string: on an RTL page twenty-one
     pointed one way and five the other, on one screen. Round 10's
     repair was a character swap where the component was wanted. */
  if (field.type !== "code_ref") {
    return (
      <span className="rounded bg-slate-100 px-1.5 py-0.5 font-mono text-[11px] text-slate-700">
        {field.type}
      </span>
    );
  }
  return (
    <span
      data-testid="dict-type-ref"
      className="rounded bg-slate-100 px-1.5 py-0.5 font-mono text-[11px] text-slate-700"
    >
      {REF_TYPE} <Arrow /> {field.ref_entity ?? "?"}
    </span>
  );
}

function FieldRow({ field }: { field: ImportFieldSpec }) {
  const { t } = useTranslation();
  return (
    <tr className="border-t border-slate-100 align-top">
      <td className="py-1.5 pe-3 text-xs text-slate-900">
        <Identifier>{field.name}</Identifier>
      </td>
      <td className="py-1.5 pe-3">
        <TypeChip field={field} />
      </td>
      <td className="py-1.5 pe-3">
        {field.required ? (
          <span
            data-testid="dict-required"
            className="rounded bg-rose-50 px-1.5 py-0.5 text-[11px] font-medium text-rose-700 ring-1 ring-rose-200"
          >
            {t("screens.dataDictionaryView.required")}
          </span>
        ) : (
          <span className="text-[11px] text-slate-400">
            {t("screens.dataDictionaryView.optional")}
          </span>
        )}
      </td>
      <td className="py-1.5 pe-3">
        {field.sama_mandated && (
          <span
            data-testid="dict-sama"
            title={field.target_column ? `Maps to ${field.target_column}` : undefined}
            className="rounded bg-indigo-50 px-1.5 py-0.5 text-[11px] font-medium text-indigo-700 ring-1 ring-indigo-200"
          >
            {t("screens.dataDictionaryView.sama")}
          </span>
        )}
      </td>
      <td className="py-1.5 text-xs text-slate-600">
        <div>{field.description}</div>
        {field.enum_values.length > 0 && (
          <div className="mt-0.5 font-mono text-[11px] text-slate-500">
            {field.enum_values.join(" · ")}
          </div>
        )}
        {/* Round 20 W13 -- a length the import enforces, stated where the
            column is described: 170 links carrying the CMDB's own
            33-character type were refused by a limit no page named. */}
        {field.max_length != null && (
          <div
            data-testid={`dict-max-length-${field.name}`}
            className="mt-0.5 text-[11px] text-slate-500"
          >
            {t("import.form.maxLength", { max: field.max_length })}
          </div>
        )}
        {field.example && (
          <div className="mt-0.5 text-[11px] text-slate-400">
            {t("screens.dataDictionaryView.eG")} {field.example}
          </div>
        )}
      </td>
    </tr>
  );
}

function EntityCard({ entity }: { entity: ImportEntityDictionary }) {
  const { t } = useTranslation();
  const toast = useToast();
  const [downloading, setDownloading] = useState(false);

  const onDownload = () => {
    setDownloading(true);
    downloadImportTemplate(entity.kind)
      .then(() => {
        toast.push({
          tone: "success",
          // Round 18 F10 -- from the catalogue.
          message: t("screens.dataDictionaryView.templateDownloaded", {
            entity: entity.label,
          }),
        });
      })
      .catch((err: unknown) => {
        toast.push({
          tone: "error",
          message: t("screens.dataDictionaryView.templateDownloadFailed", {
            entity: entity.label,
          }),
          detail: err instanceof Error ? err.message : String(err),
        });
      })
      .finally(() => setDownloading(false));
  };

  return (
    <section
      data-testid={`dict-entity-${entity.kind}`}
      className="rounded-lg border border-slate-200 bg-white p-4"
    >
      <header className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
        <div>
          <h2 className="flex items-center gap-2 text-base font-semibold text-slate-900">
            {/* Round 10 D2 -- a graph edge's heading is the two ends it
                joins, and it arrived as English prose with an ASCII
                arrow. The ends are catalogue keys now, joined by the
                mirroring separator. */}
            {(entity.label_hops ?? []).length > 0 ? (
              <PathTrail
                hops={entity.label_hops ?? []}
                keyPrefix="screens.dataDictionaryView.hop"
                fallback={entity.label}
                testid={`dict-entity-label-${entity.kind}`}
              />
            ) : (
              entity.label
            )}
            {entity.is_link && (
              <span
                data-testid={`dict-link-badge-${entity.kind}`}
                className="rounded bg-brand-brass/10 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-brand-brassDark"
              >
                {t("screens.dataDictionaryView.graphEdge")}
              </span>
            )}
            {entity.is_child && (
              <span
                data-testid={`dict-child-badge-${entity.kind}`}
                className="rounded bg-brand-brass/10 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-brand-brassDark"
              >
                {t("screens.dataDictionaryView.childRecord")}
              </span>
            )}
          </h2>
          <p className="mt-0.5 text-xs text-slate-500">
            {t("screens.dataDictionaryView.naturalKey")}{" "}
            <span className="font-mono">{entity.natural_key}</span> ·{" "}
            {entity.field_count} {t("screens.dataDictionaryView.fields")}{" "}
            {entity.required_count} {t("screens.dataDictionaryView.required2")}{" "}
            {entity.sama_mandated_count} {t("screens.dataDictionaryView.samaMandated")}
          </p>
        </div>
        <button
          type="button"
          data-testid={`dict-template-${entity.kind}`}
          onClick={onDownload}
          disabled={downloading}
          className="rounded border border-brand-brass/40 bg-brand-brass/5 px-2.5 py-1 text-xs font-medium text-brand-brassDark hover:bg-brand-brass/10 disabled:opacity-50"
        >
          {downloading
            ? t("screens.dataDictionaryView.downloading")
            : t("screens.dataDictionaryView.downloadCsvTemplate")}
        </button>
      </header>
      <table className="w-full text-start">
        <thead>
          <tr className="text-[11px] uppercase tracking-wide text-slate-400">
            <th className="pb-1 pe-3 font-medium">
              {t("screens.dataDictionaryView.field")}
            </th>
            <th className="pb-1 pe-3 font-medium">
              {t("screens.dataDictionaryView.type")}
            </th>
            <th className="pb-1 pe-3 font-medium">
              {t("screens.dataDictionaryView.req")}
            </th>
            <th className="pb-1 pe-3 font-medium">
              {t("screens.dataDictionaryView.sama")}
            </th>
            <th className="pb-1 font-medium">
              {t("screens.dataDictionaryView.description")}
            </th>
          </tr>
        </thead>
        <tbody>
          {entity.fields.map((f) => (
            <FieldRow key={f.name} field={f} />
          ))}
        </tbody>
      </table>
    </section>
  );
}

export function DataDictionaryView() {
  const { t } = useTranslation();
  const me = useMe();
  const isAdmin = me.data?.is_admin ?? false;
  const dict = useImportDictionary({ enabled: isAdmin });

  if (!isAdmin) {
    return <AdminOnlyPanel surface="The data dictionary" />;
  }

  return (
    <article data-testid="data-dictionary-view" className="max-w-5xl">
      <header className="mb-5">
        <h1 className="text-2xl font-semibold text-slate-900">
          <MarsadIcon
            name="obligation"
            size={20}
            className="me-2 inline-block align-[-0.1em] text-brand-brassDark"
          />
          {t("screens.dataDictionaryView.dataDictionary")}
        </h1>
        <p className="mt-1 max-w-prose text-sm text-slate-600">
          {t("screens.dataDictionaryView.theFieldsMarsadExpectsForEachFoundation")}
        </p>
        <Link
          to="/admin/data-import"
          data-testid="dict-to-import"
          className="mt-2 inline-block text-sm text-brand-brassDark hover:underline"
        >
          {t("screens.dataDictionaryView.goToDataImport")}
        </Link>
      </header>

      {dict.isLoading && (
        <p data-testid="data-dictionary-loading" className="text-sm text-slate-500">
          {t("screens.dataDictionaryView.loadingDictionary")}
        </p>
      )}
      {dict.isError && (
        <p
          data-testid="data-dictionary-error"
          className="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700"
        >
          {t("screens.dataDictionaryView.couldNotLoadTheDataDictionary")}
        </p>
      )}
      {dict.data && (
        <div className="space-y-4">
          {dict.data.entities.map((e) => (
            <EntityCard key={e.kind} entity={e} />
          ))}
        </div>
      )}
      {/* Round 20 W4 -- what a bank will try to import and cannot: its
          risk register and its findings. Said here, with why and the
          route that does exist, rather than left to be discovered. */}
      {(dict.data?.not_importable ?? []).length > 0 && (
        <section
          id="not-importable"
          data-testid="dict-not-importable"
          className="mt-6 rounded-lg border border-paper-2 bg-white p-4"
        >
          <h2 className="text-base font-semibold text-slate-900">
            {t("screens.dataDictionaryView.notImportable.heading")}
          </h2>
          <ul className="mt-2 space-y-3">
            {(dict.data?.not_importable ?? []).map((n) => (
              <li key={n.kind} data-testid={`dict-not-importable-${n.kind}`}>
                <p className="text-sm font-medium text-slate-800">
                  {said(
                    t,
                    `screens.dataDictionaryView.notImportable.${n.kind}.label`,
                    n.label,
                  )}
                </p>
                <p className="mt-0.5 max-w-prose text-sm text-slate-600">
                  {said(
                    t,
                    `screens.dataDictionaryView.notImportable.${n.kind}.reason`,
                    n.reason,
                  )}
                </p>
                <p className="mt-0.5 max-w-prose text-sm text-slate-600">
                  {said(
                    t,
                    `screens.dataDictionaryView.notImportable.${n.kind}.route`,
                    n.route,
                  )}{" "}
                  {n.route_link && (
                    <Link
                      to={n.route_link}
                      className="text-brand-brassDark hover:underline"
                    >
                      {t("screens.dataDictionaryView.notImportable.open")}
                    </Link>
                  )}
                </p>
              </li>
            ))}
          </ul>
        </section>
      )}
    </article>
  );
}
